import { forwardRef, useContext, useState } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { Button, InlineNotification } from '../../components';
import styles from './EParakstsBox.module.css';
import i18n, { t } from 'i18next';
import * as Config from 'Config';
import ClientDataContext from '../../store/client-data-context';
import { EpsErrorCode, EpsInitRequest, EpsInitResponseError } from '../../common/types/eps-init-request';

interface EParakstsBoxProps {
  canStart: boolean;
  socketErrorCode: string;
  setSocketErrorCode: (value: string) => void;
}

export const EParakstsBox = forwardRef<HTMLDivElement, EParakstsBoxProps>(
  ({ canStart, socketErrorCode, setSocketErrorCode }: EParakstsBoxProps, ref) => {
    const clientCtx = useContext(ClientDataContext);
    const epsLocale = i18n.language?.split('-')[0].toLowerCase() === 'lv' ? 'lv' : 'en';
    const [loading, setLoading] = useState(false);

    const startAuthentication = async () => {
      setLoading(true);
      setSocketErrorCode('');

      const res = await fetch(Config.EPS_AUTH_URL, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          _csrf: clientCtx.client._csrf,
          language: epsLocale,
        } as EpsInitRequest),
        redirect: 'manual'
      });

      setLoading(false);
      if (!res.ok) {
        setSocketErrorCode(EpsErrorCode.INTERNAL_ERROR);
        return;
      }

      if (res.status === 200) {
        const location = res.headers.get('Location');
        if (location) {
          window.location.href = location;
          return;
        }
      }

      const errorData = await res.json();
      if (errorData && errorData.error) {
        setSocketErrorCode(getErrorCode(errorData.error));
      } else {
        setSocketErrorCode(EpsErrorCode.EPS_ERROR);
      }
    };

    function getErrorCode(responseError: string) {
      const error = responseError.toUpperCase();
      switch (error) {
        case EpsInitResponseError.AUTHORIZATION_REQUEST_ERROR:
        case EpsInitResponseError.INVALID_REQUEST:
          return EpsErrorCode.EPS_ERROR;
        case EpsInitResponseError.SERVER_ERROR:
          return EpsErrorCode.INTERNAL_ERROR;
        default:
          return EpsErrorCode.EPS_ERROR;
      }
    }

    return (
      <div className={styles.epsBox} ref={ref}>
        <Stack
          spacing={2}
          sx={{
            alignItems: 'stretch',
            width: '100%'
          }}>
          <Typography variant="body1" sx={{
            textAlign: 'center'
          }}>
            {t('form.eps.description')}
          </Typography>

          {loading ? (
            <Stack
              direction="row"
              sx={{
                mb: 2,
                justifyContent: 'center'
              }}>
              <CircularProgress color="primary" />
            </Stack>
          ) : (
            <Button
              type="button"
              appearance="primary"
              size="m"
              disabled={!canStart || !clientCtx.client._csrf}
              data-testid="epsStartBtn"
              onClick={startAuthentication}
            >
              <span className={styles.buttonContent}>{t('form.eps.startBtn')}</span>
            </Button>
          )}
          {socketErrorCode && (
            <InlineNotification appearance="error">
              <div {...{ 'data-testid': 'formError' }}>{t(`form.socketError.${socketErrorCode.toUpperCase()}.text`)}</div>
            </InlineNotification>
          )}
        </Stack>
      </div>
    );
  }
);

EParakstsBox.displayName = 'EParakstsBox';
